import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CheckCircle, EnvelopeSimple, Eye, Warning } from "@phosphor-icons/react";
import { lies, schick } from "../api/client";
import type { EmpfaengerVorschlag, Versand } from "../api/types";
import { datum } from "../lib/format";
import { Dialog, Meldung } from "./Bausteine";

/**
 * Ein Ausgangsschreiben an den Verteiler schicken (SPEC §11.2). Vorgewählt ist, wer es
 * bekommen sollte (Verteiler der Besprechung bzw. AN und Bauherr); die übrigen Beteiligten
 * mit Adresse stehen darunter zum Anhaken, eine weitere Adresse lässt sich eintippen.
 * Wer es schon bekommen hat, steht dabei — ein zweiter Versand ist möglich, aber sichtbar.
 */
export function VersandDialog({ schreibenId, titel, zu }: { schreibenId: string; titel: string; zu: () => void }) {
  const qc = useQueryClient();
  const vorschlag = useQuery({
    queryKey: ["empfaenger", schreibenId],
    queryFn: () => lies<EmpfaengerVorschlag[]>(`/schreiben/${schreibenId}/empfaenger`),
  });
  const [wahl, setWahl] = useState<Record<string, boolean>>({});
  const [weitere, setWeitere] = useState("");
  const [nachricht, setNachricht] = useState("");
  useEffect(() => {
    if (vorschlag.data) setWahl(Object.fromEntries(vorschlag.data.map((v) => [v.email, v.vorgewaehlt && !v.schon_verschickt])));
  }, [vorschlag.data]);

  const zusatz = weitere.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter((x) => x.includes("@"));
  const empfaenger = [
    ...(vorschlag.data ?? []).filter((v) => wahl[v.email]).map((v) => ({ email: v.email, name: v.name })),
    ...zusatz.filter((e) => !wahl[e]).map((email) => ({ email, name: "" })),
  ];
  const senden = useMutation({
    mutationFn: () => schick<Versand[]>(`/schreiben/${schreibenId}/versand`, { empfaenger, nachricht }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schreiben"] });
      qc.invalidateQueries({ queryKey: ["versand", schreibenId] });
      qc.invalidateQueries({ queryKey: ["empfaenger", schreibenId] });
    },
  });

  if (senden.data) {
    const gut = senden.data.filter((v) => v.status === "verschickt").length;
    return (
      <Dialog offen zu={zu} titel="Versendet" fuss={<button className="knopf haupt" onClick={zu}>Fertig</button>}>
        <p style={{ marginTop: 0 }}>{gut} von {senden.data.length} Nachrichten sind hinausgegangen.</p>
        <div className="liste">{senden.data.map((v) => <VersandZeile key={v.id} v={v} />)}</div>
        <AblageErgebnis schreibenId={schreibenId} />
      </Dialog>
    );
  }
  return (
    <Dialog offen zu={zu} titel={`Versenden: ${titel}`} fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!empfaenger.length || senden.isPending} onClick={() => senden.mutate()}>
        <EnvelopeSimple size={16} /> {senden.isPending ? "wird verschickt …" : `An ${empfaenger.length} senden`}
      </button>
    </>}>
      <p className="mute" style={{ marginTop: 0 }}>Jede Person bekommt eine eigene Mail mit dem PDF und einem
        persönlichen Abruflink. Versand und erster Abruf werden festgehalten (Zugang für die Einwandfrist).</p>
      <Meldung fehler={vorschlag.error ?? senden.error} />
      <div className="liste">
        {vorschlag.data?.map((v) => (
          <label key={v.email} className="zeile versand-wahl">
            <input type="checkbox" checked={!!wahl[v.email]} onChange={(e) => setWahl({ ...wahl, [v.email]: e.target.checked })} />
            <div className="haupt"><div>{v.name || v.email}</div><div className="mute">{v.email}</div></div>
            {v.schon_verschickt ? <span className="chip gut">schon am {datum(v.schon_verschickt)}</span> : <span />}
          </label>
        ))}
        {vorschlag.data?.length === 0 && <div className="leer">Bei den Beteiligten ist keine E-Mail-Adresse hinterlegt.</div>}
      </div>
      <label className="feld" style={{ marginTop: 10 }}><span>Weitere Adressen (mit Komma getrennt)</span>
        <input className="eingabe" value={weitere} onChange={(e) => setWeitere(e.target.value)} placeholder="name@firma.de" /></label>
      <label className="feld" style={{ marginTop: 10 }}><span>Zusatz in der Mail (optional)</span>
        <textarea className="eingabe" rows={2} value={nachricht} onChange={(e) => setNachricht(e.target.value)} /></label>
      <MailVorschau schreibenId={schreibenId} nachricht={nachricht} />
    </Dialog>
  );
}

export function VersandZeile({ v }: { v: Versand }) {
  return (
    <div className="zeile">
      {v.status === "verschickt" ? <CheckCircle size={18} color="var(--good)" /> : <Warning size={18} color="var(--error)" />}
      <div className="haupt">
        <div>{v.name || v.email}</div>
        <div className="mute">
          {v.email} · {v.status === "verschickt" ? `verschickt ${datum(v.versandt_am)}${v.mit_anhang ? "" : " (nur Link)"}`
            : v.status === "zurueckgehalten" ? "Testbetrieb: zurückgehalten" : v.fehler}
        </div>
      </div>
      {v.erster_abruf ? <span className="chip gut"><Eye size={13} /> geöffnet {datum(v.erster_abruf)}</span> : <span />}
    </div>
  );
}

/** Der Versandstand eines Schreibens, aufklappbar im Ausgang. */
export function VersandListe({ schreibenId }: { schreibenId: string }) {
  const liste = useQuery({ queryKey: ["versand", schreibenId], queryFn: () => lies<Versand[]>(`/schreiben/${schreibenId}/versand`) });
  if (!liste.data?.length) return <div className="mute" style={{ padding: "6px 0" }}>Noch nicht verschickt.</div>;
  return <div className="liste">{liste.data.map((v) => <VersandZeile key={v.id} v={v} />)}</div>;
}

/** So sieht die Mail aus (SPEC §11.3): Begleittext und Signatur aus „Verwaltung → Versandmail“. */
function MailVorschau({ schreibenId, nachricht }: { schreibenId: string; nachricht: string }) {
  const [offen, setOffen] = useState(false);
  const [wort, setWort] = useState(nachricht);
  useEffect(() => {
    const t = setTimeout(() => setWort(nachricht), 400);
    return () => clearTimeout(t);
  }, [nachricht]);
  const v = useQuery({
    queryKey: ["mailvorschau", schreibenId, wort],
    queryFn: () => schick<{ betreff: string; absender: string; text: string }>(`/schreiben/${schreibenId}/vorschau`, { nachricht: wort }),
    enabled: offen,
  });
  return (
    <details className="platzhalter" style={{ marginTop: 10 }} onToggle={(e) => setOffen((e.target as HTMLDetailsElement).open)}>
      <summary className="mute">Vorschau der Mail</summary>
      {v.data && <>
        <div className="mute" style={{ margin: "8px 0 4px" }}>Von: {v.data.absender} · Betreff: {v.data.betreff}</div>
        <pre className="mail-vorschau">{v.data.text}</pre>
      </>}
      <p className="mute">Begleittext und Signatur: <Link to="/verwaltung">Verwaltung → Versandmail</Link>.</p>
    </details>
  );
}

/** Nach dem Versand: liegt die PDF-Kopie im Projektordner auf dem Büro-Server (SPEC §11.4)? */
function AblageErgebnis({ schreibenId }: { schreibenId: string }) {
  const stand = useQuery({
    queryKey: ["serverablage", schreibenId],
    queryFn: () => lies<{ ordner: string; aktiv: boolean; letzte: { pfad: string; fehler: string } | null }>(`/schreiben/${schreibenId}/serverablage`),
  });
  const d = stand.data;
  if (!d || !d.ordner) return null;
  if (!d.aktiv) return <p className="mute">Die Ablage auf dem Büro-Server ist noch nicht eingerichtet – keine Kopie im Projektordner.</p>;
  if (d.letzte?.pfad) return <p className="gespeichert">PDF-Kopie abgelegt: <code>{d.letzte.pfad}</code></p>;
  if (d.letzte?.fehler) return <div className="meldung">PDF-Kopie auf dem Server fehlgeschlagen: {d.letzte.fehler}</div>;
  return null;
}
